import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Ticket, Search, TrendingDown, PackageCheck, Users, Loader2, Building2, User as UserIcon, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { SellDayPassPackageModal } from './SellDayPassPackageModal';
import { MarkDayPassUsedModal } from './MarkDayPassUsedModal';

export const DayPassOverviewTab = ({ api, isAdmin, canDelete = false, venues = [], myVenues = [] }) => {
  const [packages, setPackages] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [detailPackage, setDetailPackage] = useState(null);
  const [detailTxns, setDetailTxns] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showSellModal, setShowSellModal] = useState(false);
  const [showMarkUsedModal, setShowMarkUsedModal] = useState(false);
  const [markUsedClient, setMarkUsedClient] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, packagesRes] = await Promise.all([
        api.get('/day-pass-packages/overview'),
        api.get('/day-pass-packages'),
      ]);
      setSummary(overviewRes.data.summary || null);
      setPackages(Array.isArray(packagesRes.data) ? packagesRes.data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load Bulk Day Pass overview');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openPackageTxns = async (pkg) => {
    setDetailPackage(pkg);
    setDetailTxns([]);
    setDetailLoading(true);
    try {
      const res = await api.get(`/day-pass-packages/${pkg.id}/transactions`);
      setDetailTxns(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load transactions');
    } finally {
      setDetailLoading(false);
    }
  };

  const deletePackage = async (pkg) => {
    if (!window.confirm(`Permanently delete this package of ${pkg.quantity_purchased} Day Passes for ${pkg.client_name || 'this client'}? Its transaction history is removed too. This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/day-pass-packages/${pkg.id}`);
      toast.success('Package deleted');
      setDetailPackage(null);
      setDetailTxns([]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete package');
    } finally {
      setDeleting(false);
    }
  };

  const openMarkUsedFor = (p) => {
    setMarkUsedClient({
      type: p.organization_id ? 'organization' : 'user',
      id: p.organization_id || p.user_id,
      name: p.client_name || 'Unknown client',
      email: p.client_email || '',
    });
    setShowMarkUsedModal(true);
  };

  const filtered = packages.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.client_name || '').toLowerCase().includes(q) || (p.client_email || '').toLowerCase().includes(q);
  });

  const fmt = (v) => Number(v || 0).toLocaleString('en-IN');
  const venueName = (id) => venues.find((v) => v.id === id)?.name || id;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="space-y-6" data-testid="day-pass-overview-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-heading">Bulk Day Pass Packages</h2>
          <p className="text-sm text-muted-foreground">Bulk Day-Pass packages sold, used and remaining across {isAdmin ? 'all clients' : 'your assigned centres'}.</p>
        </div>
        {!isAdmin && (
          <Button onClick={() => setShowSellModal(true)} data-testid="day-pass-sell-open-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Sell a Package
          </Button>
        )}
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><PackageCheck className="w-3 h-3" /> Total Purchased</p>
            <p className="text-2xl font-bold mt-1" data-testid="day-pass-summary-purchased">{fmt(summary.total_purchased)}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><Ticket className="w-3 h-3" /> Total Remaining</p>
            <p className="text-2xl font-bold mt-1 text-green-700" data-testid="day-pass-summary-remaining">{fmt(summary.total_remaining)}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><TrendingDown className="w-3 h-3" /> Total Used</p>
            <p className="text-2xl font-bold mt-1 text-orange-600" data-testid="day-pass-summary-used">{fmt(summary.total_used)}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><Users className="w-3 h-3" /> Clients with Packages</p>
            <p className="text-2xl font-bold mt-1">{fmt(summary.clients)}</p>
          </CardContent></Card>
        </div>
      )}

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" placeholder="Search by name or email…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="day-pass-search" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <Ticket className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-muted-foreground">No Bulk Day Pass packages found.</p>
        </CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium">Client</th>
                    <th className="px-4 py-3 font-medium">Valid At</th>
                    <th className="px-4 py-3 font-medium">Sold By</th>
                    <th className="px-4 py-3 font-medium text-right">Purchased</th>
                    <th className="px-4 py-3 font-medium text-right">Used</th>
                    <th className="px-4 py-3 font-medium text-right">Remaining</th>
                    <th className="px-4 py-3 font-medium">Expiry</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => {
                    const isScheduled = p.status === 'active' && p.start_date && new Date(p.start_date) > new Date();
                    return (
                      <tr
                        key={p.id}
                        className="border-t border-border hover:bg-muted/30 cursor-pointer transition-colors"
                        onClick={() => openPackageTxns(p)}
                        data-testid={`day-pass-package-row-${p.id}`}
                      >
                        <td className="px-4 py-3">
                          {isAdmin ? (
                            <div className="flex items-center gap-2">
                              {p.organization_id ? <Building2 className="w-4 h-4 text-primary shrink-0" /> : <UserIcon className="w-4 h-4 text-muted-foreground shrink-0" />}
                              <div className="min-w-0">
                                <div className="font-medium truncate">{p.client_name || 'Unknown client'}</div>
                                {p.client_email && <div className="text-xs text-muted-foreground truncate">{p.client_email}</div>}
                              </div>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="flex items-center gap-2 text-left hover:underline decoration-primary/50 underline-offset-2"
                              onClick={(e) => { e.stopPropagation(); openMarkUsedFor(p); }}
                              title="Mark a Day Pass as used for this client"
                              data-testid={`day-pass-client-link-${p.id}`}
                            >
                              {p.organization_id ? <Building2 className="w-4 h-4 text-primary shrink-0" /> : <UserIcon className="w-4 h-4 text-muted-foreground shrink-0" />}
                              <div className="min-w-0">
                                <div className="font-medium truncate">{p.client_name || 'Unknown client'}</div>
                                {p.client_email && <div className="text-xs text-muted-foreground truncate">{p.client_email}</div>}
                              </div>
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1 max-w-[220px]">
                            {(p.venue_ids && p.venue_ids.length > 0) ? p.venue_ids.map((vid) => (
                              <Badge key={vid} variant="outline" className="text-xs">{venueName(vid)}</Badge>
                            )) : <span className="text-xs text-muted-foreground">All venues</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground truncate max-w-[140px]">{p.sold_by_name || '—'}</td>
                        <td className="px-4 py-3 text-right font-mono">{fmt(p.quantity_purchased)}</td>
                        <td className="px-4 py-3 text-right font-mono text-orange-600">{fmt(p.quantity_used)}</td>
                        <td className="px-4 py-3 text-right font-mono font-semibold text-green-700">{fmt(p.quantity_remaining)}</td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDate(p.expiry_date)}</td>
                        <td className="px-4 py-3">
                          {isScheduled ? (
                            <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-700 border-amber-500/30">Scheduled</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs capitalize">{p.status}</Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!detailPackage} onOpenChange={(v) => { if (!v) { setDetailPackage(null); setDetailTxns([]); } }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              {detailPackage?.organization_id ? <Building2 className="w-5 h-5 text-primary" /> : <UserIcon className="w-5 h-5 text-primary" />}
              {detailPackage?.client_name || 'Unknown client'}
            </DialogTitle>
          </DialogHeader>
          {detailPackage && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                {(detailPackage.venue_ids && detailPackage.venue_ids.length > 0) ? detailPackage.venue_ids.map((vid) => (
                  <Badge key={vid} variant="outline" className="text-xs">{venueName(vid)}</Badge>
                )) : <span className="text-xs text-muted-foreground">All venues</span>}
                <span className="text-xs text-muted-foreground">
                  Sold {fmtDate(detailPackage.created_at)} by {detailPackage.sold_by_name || 'Sales'} · Expires {fmtDate(detailPackage.expiry_date)}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-muted/40 rounded p-3">
                  <p className="text-xs text-muted-foreground">Purchased</p>
                  <p className="text-xl font-bold font-mono">{detailPackage.quantity_purchased}</p>
                </div>
                <div className="bg-orange-500/10 rounded p-3">
                  <p className="text-xs text-orange-700">Used</p>
                  <p className="text-xl font-bold font-mono text-orange-700">{detailPackage.quantity_used}</p>
                </div>
                <div className="bg-green-500/10 rounded p-3">
                  <p className="text-xs text-green-700">Remaining</p>
                  <p className="text-xl font-bold font-mono text-green-700">{detailPackage.quantity_remaining}</p>
                </div>
              </div>
              <div>
                <h3 className="font-medium mb-2">Transaction History</h3>
                {detailLoading ? (
                  <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
                ) : detailTxns.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No transactions yet.</p>
                ) : (
                  <div className="border rounded divide-y max-h-[40vh] overflow-y-auto">
                    {detailTxns.map((t) => (
                      <div key={t.id} className="p-3 flex items-start justify-between gap-3" data-testid={`day-pass-txn-${t.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-xs">{t.transaction_type}</Badge>
                            <span className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                            {t.venue_id && (
                              <Badge variant="outline" className="text-xs">{venueName(t.venue_id)}</Badge>
                            )}
                            {t.restored_to_expired_package && (
                              <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-700 border-amber-500/30">restored after expiry</Badge>
                            )}
                          </div>
                          <p className="text-sm mt-1 truncate">{t.description}</p>
                        </div>
                        <span className={`font-mono font-semibold ${t.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {t.amount >= 0 ? '+' : ''}{t.amount}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {canDelete && (
                <div className="flex justify-end border-t border-border pt-4">
                  <Button variant="destructive" size="sm" disabled={deleting} onClick={() => deletePackage(detailPackage)} data-testid="day-pass-delete-btn">
                    {deleting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
                    Delete package
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {!isAdmin && (
        <>
          <SellDayPassPackageModal
            open={showSellModal}
            onClose={() => setShowSellModal(false)}
            api={api}
            onSold={fetchData}
            venues={venues}
          />
          <MarkDayPassUsedModal
            open={showMarkUsedModal}
            onClose={() => { setShowMarkUsedModal(false); setMarkUsedClient(null); }}
            api={api}
            myVenues={myVenues}
            onMarked={fetchData}
            initialClient={markUsedClient}
          />
        </>
      )}
    </div>
  );
};

export default DayPassOverviewTab;
